// Derived device fields shared between UI (DeviceModel) and Node (getDeviceTableData).
// Keeps both sides in sync to allow predictable display & filtering behavior.
// UI passes DeviceModel's observable `attributes`; Node passes a plain JSON row.
// These must be pure functions.

import { Address4, Address6 } from 'ip-address';
import { AllDeviceClass, DEFAULT_DEVICE_CLASS } from '@kentik/ui-shared/nms/deviceMetadata';

export type DeviceFields = Record<string, unknown>;

function includesKentikAgent(value: unknown): boolean {
  if (typeof value === 'string') {
    return value.includes('kentik_agent');
  }
  if (Array.isArray(value)) {
    return value.includes('kentik_agent');
  }
  return false;
}

const DEVICE_TYPE_ACRONYMS: Record<string, string> = {
  router: 'Router',
  host: 'cHst',
  'host-nprobe-basic': 'nHst',
  'host-nprobe-dns-www': 'KPb'
};

export function deviceType(fields: DeviceFields): string | undefined {
  return (fields.device_subtype as string | undefined) || DEVICE_TYPE_ACRONYMS[fields.device_type as string];
}

export function rawFps(fields: DeviceFields): number {
  const fps = Number(fields.raw_fps);
  return Number.isNaN(fps) ? 0 : fps;
}

export function flowSource(fields: DeviceFields): 'none' | 'client' | 'agent' {
  if (!rawFps(fields)) {
    return 'none';
  }
  const stats = (fields.stats as Record<string, unknown>) || {};
  const isAgent = parseInt(String(stats.IsAgent), 10);
  if (!isAgent) {
    return 'client';
  }
  return isAgent === 1 ? 'agent' : 'none';
}

export function flowType(fields: DeviceFields): unknown {
  const stats = (fields.stats as Record<string, unknown>) || {};
  return stats.FlowType;
}

// Not shared. Row's `labels` already carries resolved {id, name, color} objects,
// while DeviceModel looks names up via the $labels store.
export function appliedLabels(fields: DeviceFields): string {
  const labels = (fields.labels as { name?: string }[] | null | undefined) || [];
  return labels.length ? labels.map((label) => label.name).join(' ') : '';
}

export function isICMP(fields: DeviceFields): boolean {
  return !!fields.ranger_icmp_ip;
}

export function flowConfigured(fields: DeviceFields): boolean {
  const sendingIps = Array.isArray(fields.sending_ips) ? fields.sending_ips : [];
  return sendingIps.length > 0 && !!fields.device_sample_rate;
}

export function isICMPOnly(fields: DeviceFields): boolean {
  return isICMP(fields) && !flowConfigured(fields);
}

export function isNMS(fields: DeviceFields): boolean {
  return includesKentikAgent(fields.snmp_collection_method);
}

export function nmsConfigured(fields: DeviceFields): boolean {
  return isNMS(fields) || isICMP(fields);
}

export function isEnrichingFlowWithUA(fields: DeviceFields): boolean {
  const value = fields.snmp_collection_method;
  if (typeof value === 'string') {
    return value.includes('kentik_agent_flow_only');
  }
  if (Array.isArray(value)) {
    return value.includes('kentik_agent_flow_only');
  }
  return false;
}

export function effectiveMonitoringTemplateId(
  fields: DeviceFields,
  defaultTemplateId: string | number | null | undefined
): unknown {
  const id = fields.monitoring_template_id;
  if (!isNMS(fields) || isEnrichingFlowWithUA(fields) || id) {
    return id;
  }
  return defaultTemplateId;
}

export function deviceClass(fields: DeviceFields): string | null {
  const classification = fields.class;

  if (isICMPOnly(fields)) {
    return AllDeviceClass.Icmp;
  }
  if (isICMP(fields)) {
    return AllDeviceClass.FlowIcmp;
  }
  if (!isNMS(fields)) {
    return AllDeviceClass.Flow;
  }
  if (!classification) {
    // Default class for unclassified NMS devices
    return DEFAULT_DEVICE_CLASS;
  }
  if (!(Object.values(AllDeviceClass) as string[]).includes(classification as string)) {
    // Classification isn't one the UI knows how to display
    return null;
  }
  return classification as string;
}

export function hasStreamingTelemetry(fields: DeviceFields): boolean {
  return includesKentikAgent(fields.streaming_telemetry_collection_method);
}

export function stEnabled(fields: DeviceFields): boolean {
  const conf = fields.device_gnmi_v1_conf as { dialoutServer?: string } | null | undefined;
  return !!conf && conf.dialoutServer === 'auto';
}

export function hasKentikAgent(fields: DeviceFields): boolean {
  return !!fields.device_kentik_agent_id;
}

export function hasKproxyAgent(fields: DeviceFields): boolean {
  return !!fields.device_kproxy_agent_id;
}

export function snmpStatus(fields: DeviceFields): boolean {
  return Boolean(fields.snmp_enabled) && Boolean(fields.snmp_healthy);
}

export function snmpEnrichmentStatus(fields: DeviceFields): boolean | 'unknown' {
  return isICMPOnly(fields) ? 'unknown' : snmpStatus(fields);
}

export function stEnrichmentStatus(fields: DeviceFields): 'unknown' | 'up' | 'down' {
  if (!stEnabled(fields) || isICMPOnly(fields)) {
    return 'unknown';
  }
  return fields.st_healthy ? 'up' : 'down';
}

export function snmpNmsStatus(fields: DeviceFields): unknown {
  if (!isNMS(fields) || isICMPOnly(fields)) {
    return 'unknown';
  }
  return fields.nms_status;
}

export function stNmsStatus(fields: DeviceFields): 'unknown' | 'up' | 'down' {
  if (!isNMS(fields) || isICMPOnly(fields)) {
    return 'unknown';
  }
  return hasStreamingTelemetry(fields) ? 'up' : 'down';
}

export function icmpStatus(fields: DeviceFields): unknown {
  return isICMP(fields) ? fields.nms_status : 'unknown';
}

// Ordering-only, not a display value — IPv4/IPv6 sort numerically instead of lexicographically.
export function sendingIpSortValue(fields: DeviceFields): string | undefined {
  const firstIp = (fields.sending_ips as string[] | undefined)?.[0];
  const IPV4_SORT_DIGITS = 10;
  const IPV6_SORT_DIGITS = 39;

  if (!firstIp) {
    return undefined;
  }

  try {
    const v4 = new Address4(firstIp.trim());
    if (v4.isCorrect()) {
      return v4.bigInteger().toString().padStart(IPV4_SORT_DIGITS, '0');
    }
  } catch {
    // fall through to v6
  }

  try {
    // ip-address v6 is very strict with its correctness check; treat any successful parse as valid.
    const v6 = new Address6(firstIp.trim());
    return v6.bigInteger().toString().padStart(IPV6_SORT_DIGITS, '0');
  } catch {
    return undefined;
  }
}

export function statusDisplay(fields: DeviceFields): string {
  const status = fields.nms_status as string | null | undefined;
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
}

export interface BgpLinkStatus {
  established: boolean;
  configured: boolean;
}

export interface BgpStatus {
  v4: BgpLinkStatus;
  v6: BgpLinkStatus;
}

export function bgpStatus(fields: DeviceFields): BgpStatus {
  const stats = (fields.stats as Record<string, unknown>) || {};
  const metadata = (fields.metadata as Record<string, unknown>) || {};

  if (fields.device_bgp_type === 'device') {
    return {
      v4: {
        established: parseInt(String(stats.RibSessionState), 10) === 1,
        configured: !!metadata.device_bgp_neighbor_ip
      },
      v6: {
        established: parseInt(String(stats.RibSessionState6), 10) === 1,
        configured: !!metadata.device_bgp_neighbor_ip6
      }
    };
  }

  if (fields.device_bgp_type === 'other_device') {
    return { v4: { established: true, configured: true }, v6: { established: true, configured: true } };
  }

  return { v4: { established: false, configured: false }, v6: { established: false, configured: false } };
}

export function percentClassified(fields: DeviceFields): number {
  const ic = fields.interface_classification as Record<string, unknown> | null | undefined;
  if (!ic) {
    return 0;
  }
  const classified = Number(ic.interfacesClassifiedCount ?? 0);
  const total = Number(ic.interfaceCount ?? 0);
  return total === 0 ? 0 : Math.round((classified / total) * 100);
}

export function sampleRate(fields: DeviceFields): number | null {
  const stats = (fields.stats as Record<string, unknown>) || {};
  // Number(null) is 0, not NaN — must reject null explicitly or it'd be mistaken for real data.
  if (stats.sampleRate === null || stats.sampleRate === undefined) {
    return null;
  }
  const rate = Number(stats.sampleRate);
  return Number.isFinite(rate) ? rate : null;
}

export function credentials(fields: DeviceFields, hasCommandAccessValue: boolean): string[] {
  const result: string[] = [];

  if (fields.device_status !== 'V') {
    return result;
  }

  if (isNMS(fields)) {
    const options = fields.kentik_agent_snmp_options as { credential_name?: string } | null | undefined;
    const name = options?.credential_name ?? fields.agent_snmp_credential_name;
    if (name) {
      result.push(name as string);
    }
  }

  if (hasStreamingTelemetry(fields)) {
    const options = fields.streaming_telemetry_collection_options as { credential_name?: string } | null | undefined;
    const name = options?.credential_name ?? fields.agent_st_credential_name;
    if (name) {
      result.push(name as string);
    }
  }

  if (hasCommandAccessValue) {
    const options = fields.deviceconf_options as { credential_name?: string } | null | undefined;
    const name = options?.credential_name ?? fields.deviceconf_credential_name;
    if (name) {
      result.push(name as string);
    }
  }

  return result;
}

export function hasCommandAccess(fields: DeviceFields, hasPermission: boolean): boolean {
  const options = fields.deviceconf_options as { enabled?: boolean; agent_id?: string } | null | undefined;
  return (
    hasPermission &&
    (!!options?.enabled || !!fields.deviceconf_enabled || !!options?.agent_id || !!fields.deviceconf_agent_id)
  );
}

export function hasConfigFetch(fields: DeviceFields, hasCommandAccessValue: boolean): boolean {
  const options = fields.deviceconf_options as { fetch_enabled?: boolean; fetch_interval?: number } | null | undefined;
  return (
    hasCommandAccessValue &&
    (!!options?.fetch_enabled ||
      !!fields.deviceconf_fetch_enabled ||
      !!options?.fetch_interval ||
      !!fields.deviceconf_fetch_interval)
  );
}

// `options != null` (not `?.`/`??`) is deliberate: a present-but-empty deviceconf_options object
// should NOT fall back to the flat field just because command_enabled happens to be unset on it.
export function aiAdvisorAccess(fields: DeviceFields, hasCommandAccessValue: boolean): boolean {
  if (!hasCommandAccessValue) {
    return false;
  }
  const options = fields.deviceconf_options as { command_enabled?: boolean } | null | undefined;
  const commandEnabled = options != null ? options.command_enabled : fields.deviceconf_command_enabled;
  return commandEnabled === true;
}
